import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { PerformancePeriod } from 'app/library/performance-period/models/performance-period.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { SimpleEntity } from 'app/projects/shared/src/lib/models/simple-entity.model';
import { Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreatePerformanceTeamReview,
    CreatePerformanceTeamReviewFailure,
    CreatePerformanceTeamReviewSuccess,
    DeletePerformanceTeamReview,
    DeletePerformanceTeamReviewFailure,
    DeletePerformanceTeamReviewSuccess,
    FetchAvailablePerformanceTeamTemplateTypes,
    FetchAvailablePerformanceTeamTemplateTypesFailure,
    FetchAvailablePerformanceTeamTemplateTypesSuccess,
    FetchPerformanceTeamMemberOverveview,
    FetchPerformanceTeamMemberOverveviewFailure,
    FetchPerformanceTeamMemberOverveviewSuccess,
    FetchPerformanceTeamOverveview,
    FetchPerformanceTeamOverveviewFailure,
    FetchPerformanceTeamOverveviewSuccess,
    FetchPerformanceTeamPeriods,
    FetchPerformanceTeamPeriodsFailure,
    FetchPerformanceTeamPeriodsSuccess,
    FetchPerformanceTeamReview,
    FetchPerformanceTeamReviewFailure,
    FetchPerformanceTeamReviewSuccess,
    FetchPerformanceTeamTemplateTypes,
    FetchPerformanceTeamTemplateTypesFailure,
    FetchPerformanceTeamTemplateTypesSuccess,
    UpdatePerformanceTeamParticipants,
    UpdatePerformanceTeamParticipantsFailure,
    UpdatePerformanceTeamParticipantsSuccess,
} from '../actions';
import {
    FetchPerformanceTeamReviews,
    FetchPerformanceTeamReviewsFailure,
    FetchPerformanceTeamReviewsSuccess,
    UpdatePerformanceTeamReview,
    UpdatePerformanceTeamReviewFailure,
    UpdatePerformanceTeamReviewSuccess,
} from '../actions/index';
import { IPerformanceTeamReviewActionPayload } from '../interfaces/create-performance-team-review.interface';
import { PerformanceTeamMemberOverview } from '../models/performance-team-member-overview.model';
import { PerformanceTeamOverview } from '../models/performance-team-overview.model';
import { PerformanceTeamReview } from '../models/performance-team-review.model';
import { PerformanceTeamReviewState } from '../models/performance-team-review.state';

@Injectable()
export class PerformanceTeamReviewService {
    @Select(PerformanceTeamReviewState.getEntity)
    performanceTeamReview$: Observable<PerformanceTeamReview>;

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getPerformanceTeamReviewById(performanceTeamReviewId: string): PerformanceTeamReview {
        return PerformanceTeamReviewState.getEntity(this._store.selectSnapshot(PerformanceTeamReviewState), performanceTeamReviewId);
    }

    getPerformanceTeamReviews(): PerformanceTeamReview[] {
        return PerformanceTeamReviewState.getEntities(this._store.selectSnapshot(PerformanceTeamReviewState));
    }

    async fetchPerformanceTeamReview(id: string): Promise<boolean> {
        this._store.dispatch(new FetchPerformanceTeamReview(id));

        return await this._actionHelper.race(FetchPerformanceTeamReviewSuccess, FetchPerformanceTeamReviewFailure).toPromise();
    }

    async fetchPerformanceTeamReviews(
        teamId: string,
        pageSize?: number,
        pageNumber?: number,
        search?: string,
        type?: string,
        periodUnderReview?: string,
        goalSettingPeriod?: string,
        userId?: string
    ): Promise<boolean> {
        this._store.dispatch(new FetchPerformanceTeamReviews({ teamId, pageSize, pageNumber, search, type, periodUnderReview, goalSettingPeriod, userId }));

        return await this._actionHelper.race(FetchPerformanceTeamReviewsSuccess, FetchPerformanceTeamReviewsFailure).toPromise();
    }

    async createPerformanceTeamReview(performanceTeamReview: IPerformanceTeamReviewActionPayload): Promise<PerformanceTeamReview> {
        this._store.dispatch(new CreatePerformanceTeamReview(performanceTeamReview));

        return race(
            this._action$.pipe(
                ofActionDispatched(CreatePerformanceTeamReviewSuccess),
                map((action: CreatePerformanceTeamReviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(CreatePerformanceTeamReviewFailure),
                map((action: CreatePerformanceTeamReviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updatePerformanceTeamParticipants(teamReviewId: string, userIds: string[]): Promise<PerformanceTeamReview> {
        this._store.dispatch(new UpdatePerformanceTeamParticipants({ teamReviewId, userIds }));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamParticipantsSuccess),
                map((action: UpdatePerformanceTeamParticipantsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamParticipantsFailure),
                map((action: UpdatePerformanceTeamParticipantsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getPerformanceTeamReviewOverview(teamId: string): Promise<PerformanceTeamOverview> {
        this._store.dispatch(new FetchPerformanceTeamOverveview(teamId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamOverveviewSuccess),
                map((action: FetchPerformanceTeamOverveviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamOverveviewFailure),
                map((action: FetchPerformanceTeamOverveviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getPerformanceTeamMemberReviewOverview(teamId: string, userId: string): Promise<PerformanceTeamMemberOverview[]> {
        this._store.dispatch(new FetchPerformanceTeamMemberOverveview({ teamId, userId }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamMemberOverveviewSuccess),
                map((action: FetchPerformanceTeamMemberOverveviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamMemberOverveviewFailure),
                map((action: FetchPerformanceTeamMemberOverveviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updatePerformanceTeamReview(performanceTeamReviewId: string, performanceTeamReview: IPerformanceTeamReviewActionPayload): Promise<PerformanceTeamReview> {
        this._store.dispatch(new UpdatePerformanceTeamReview({ performanceTeamReviewId, performanceTeamReview }));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamReviewSuccess),
                map((action: UpdatePerformanceTeamReviewSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePerformanceTeamReviewFailure),
                map((action: UpdatePerformanceTeamReviewFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async getPerformanceTeamPeriods(teamId: string): Promise<PerformancePeriod[]> {
        this._store.dispatch(new FetchPerformanceTeamPeriods(teamId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamPeriodsSuccess),
                map((action: FetchPerformanceTeamPeriodsSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamPeriodsFailure),
                map((action: FetchPerformanceTeamPeriodsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAvailablePerformanceTeamTemplateTypes(typeId: string): Promise<SimpleEntity[]> {
        this._store.dispatch(new FetchAvailablePerformanceTeamTemplateTypes(typeId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchAvailablePerformanceTeamTemplateTypesSuccess),
                map((action: FetchAvailablePerformanceTeamTemplateTypesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAvailablePerformanceTeamTemplateTypesFailure),
                map((action: FetchAvailablePerformanceTeamTemplateTypesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchPerformanceTeamTemplateTypes(typeId: string): Promise<SimpleEntity[]> {
        this._store.dispatch(new FetchPerformanceTeamTemplateTypes(typeId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamTemplateTypesSuccess),
                map((action: FetchPerformanceTeamTemplateTypesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchPerformanceTeamTemplateTypesFailure),
                map((action: FetchPerformanceTeamTemplateTypesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async deletePerformanceTeamReview(id: string): Promise<boolean> {
        this._store.dispatch(new DeletePerformanceTeamReview(id));

        return await this._actionHelper.race(DeletePerformanceTeamReviewSuccess, DeletePerformanceTeamReviewFailure).toPromise();
    }
}
